import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSubmissions, getSubmissionDetails, deleteSubmission, batchDeleteSubmissions, logout, getDownloadUrl } from '../api';
import Layout from './Layout';
import DataTable from './DataTable';
import AllSubmissionsSkeleton from './skeletons/AllSubmissionsSkeleton';
import FileViewerModal from './FileViewerModal';
import { formatDateEST } from '../utils/dateUtils';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Form type labels with icons and colors (MUST MATCH AdminDashboard.jsx)
const getFormTypeLabel = (formType) => {
  const formTypes = {
    'Concrete Safety Daily Log': { label: 'Concrete Safety', icon: '🏗️', color: '#7c3aed' },
    'Facade Daily Log': { label: 'Facade Daily Log', icon: '🏢', color: '#db2777' },
    'Fire Safety Manager Daily Log': { label: 'Fire Safety Manager', icon: '🔥', color: '#dc2626' },
    'Site Safety Coordinator Daily Log': { label: 'Site Safety Coordinator', icon: '🏗️', color: '#0891b2' },
    'Site Safety Manager Daily Log': { label: 'Site Safety Manager', icon: '🏗️', color: '#059669' },
    'Superintendent Daily Log': { label: 'Superintendent Log', icon: '📋', color: '#9333ea' },
    'Weekly TimeSheet': { label: 'Weekly Timesheet', icon: '⏰', color: '#ea580c' },
    'Other': { label: 'Other', icon: '📄', color: '#6b7280' }
  };

  // Fallback for old 'simple' entries or empty form_type
  return formTypes[formType] || { label: formType || 'Daily Log', icon: '📄', color: '#3b82f6' };
};

function AllSubmissions({ user, onLogout }) {
  const [submissions, setSubmissions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingSubmissionId, setLoadingSubmissionId] = useState(null); // Track which submission is being loaded
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewingPDFInModal, setViewingPDFInModal] = useState(null); // {id, name, url} - PDF viewer inside modal
  const [viewingPDFStandalone, setViewingPDFStandalone] = useState(null); // {id, name, url} - PDF viewer standalone modal

  // Bulk delete state
  const [selectedIds, setSelectedIds] = useState([]); // Array of selected submission IDs
  const [isDeleting, setIsDeleting] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    worker_name: '',
    job_site: '',
    comments: '',
    from_date: '',
    to_date: '',
    form_type: '',
    page: 1
  });

  // OPTIMIZATION: Debounce timer for search filter
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for worker filter from URL on mount
  useEffect(() => {
    const workerParam = searchParams.get('worker');
    if (workerParam) {
      const newFilters = {
        ...filters,
        search: workerParam,
        page: 1
      };
      setFilters(newFilters);
      loadSubmissions(newFilters);
    } else {
      loadSubmissions();
    }
  }, []); // Only run on mount

  useEffect(() => {
    loadSubmissions();
  }, [filters.page]);

  const loadSubmissions = async (customFilters = null) => {
    setLoading(true);
    setError('');

    const filtersToUse = customFilters || filters;

    try {
      const response = await getSubmissions(filtersToUse);

      if (response.success) {
        setSubmissions(response.data);
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to load submissions');
      }

      // Check for unauthorized
      if (response.error?.includes('Unauthorized')) {
        handleLogout();
      }
    } catch (error) {
      setError('An unexpected error occurred');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // Update filter state immediately for UI responsiveness
    setFilters({
      ...filters,
      [name]: value
    });

    // OPTIMIZATION: Debounce text input fields to reduce API calls
    const debouncedFields = ['search', 'worker_name', 'job_site', 'comments'];
    if (debouncedFields.includes(name)) {
      // Clear previous timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      // Set new timer (wait 500ms after user stops typing)
      const timer = setTimeout(() => {
        // Auto-apply filter after debounce
        const newFilters = { ...filters, [name]: value, page: 1 };
        loadSubmissions(newFilters);
      }, 500);

      setSearchDebounceTimer(timer);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  const handleApplyFilters = () => {
    // Reset to page 1 and reload with new filters
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);

    // Load data with the new filters immediately
    loadSubmissions(newFilters);
  };

  const handleResetFilters = () => {
    // Reset all filters and reload
    const resetFilters = {
      search: '',
      worker_name: '',
      job_site: '',
      comments: '',
      from_date: '',
      to_date: '',
      form_type: '',
      page: 1
    };
    setFilters(resetFilters);

    // Load data with reset filters immediately
    loadSubmissions(resetFilters);
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleViewDetails = async (id) => {
    setLoadingSubmissionId(id); // Mark this submission as loading
    setLoadingDetails(true);
    setShowModal(true); // Show modal immediately with loading state
    setSelectedSubmission(null); // Clear previous data

    try {
      const response = await getSubmissionDetails(id);
      if (response.success) {
        setSelectedSubmission(response.submission);
      } else {
        alert('Failed to load submission details');
        setShowModal(false);
      }
    } catch (error) {
      alert('Failed to load submission details');
      setShowModal(false);
    } finally {
      setLoadingDetails(false);
      setLoadingSubmissionId(null); // Clear loading state
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this submission? This cannot be undone.')) {
      try {
        const response = await deleteSubmission(id);
        if (response.success) {
          alert('Submission deleted successfully');
          loadSubmissions();
          if (showModal) {
            setShowModal(false);
          }
        }
      } catch (error) {
        alert('Failed to delete submission: ' + error.message);
      }
    }
  };

  // Bulk delete functions
  const toggleSelectSubmission = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === submissions.length) {
      // Deselect all
      setSelectedIds([]);
    } else {
      // Select all on current page
      setSelectedIds(submissions.map(sub => sub.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Please select at least one submission to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedIds.length} submission(s)?\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsDeleting(true);

    try {
      // OPTIMIZATION: Use batch delete endpoint instead of looping
      const response = await batchDeleteSubmissions(selectedIds);

      if (response.success) {
        alert(response.message || `Successfully deleted ${response.deleted_count} submission(s)`);
        setSelectedIds([]); // Clear selection
        loadSubmissions(); // Reload data
      } else {
        alert('Failed to delete selected submissions');
      }
    } catch (error) {
      alert('An error occurred during bulk delete: ' + error.message);
      console.error('Bulk delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Clear selections when page changes
  useEffect(() => {
    setSelectedIds([]);
  }, [filters.page]);

  const handleDownloadFiles = async (submission) => {
    try {
      // Get submission details to get all files
      const response = await getSubmissionDetails(submission.id);

      if (!response.success) {
        throw new Error('Failed to load submission files');
      }

      const files = response.submission.files || [];

      if (files.length === 0) {
        alert('No files attached to this submission');
        return;
      }

      // Download each file
      files.forEach((file, index) => {
        setTimeout(() => {
          const link = document.createElement('a');
          link.href = getDownloadUrl(file.id);
          link.download = file.file_name;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, index * 300); // Stagger downloads by 300ms
      });

      console.log(`Downloaded ${files.length} file(s) from submission`);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download files: ' + error.message);
    }
  };


  const handleExportExcel = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.worker_name) queryParams.append('worker_name', filters.worker_name);
      if (filters.job_site) queryParams.append('job_site', filters.job_site);
      if (filters.comments) queryParams.append('comments', filters.comments);
      if (filters.from_date) queryParams.append('from_date', filters.from_date);
      if (filters.to_date) queryParams.append('to_date', filters.to_date);
      if (filters.form_type) queryParams.append('form_type', filters.form_type);

      // Create a temporary form to submit with credentials
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = `${API_BASE_URL}/export_excel.php`;
      form.target = '_blank';

      // Add query parameters as hidden inputs
      queryParams.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      alert('Failed to export Excel: ' + error.message);
    }
  };

  const handleExportPDF = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.worker_name) queryParams.append('worker_name', filters.worker_name);
      if (filters.job_site) queryParams.append('job_site', filters.job_site);
      if (filters.comments) queryParams.append('comments', filters.comments);
      if (filters.from_date) queryParams.append('from_date', filters.from_date);
      if (filters.to_date) queryParams.append('to_date', filters.to_date);
      if (filters.form_type) queryParams.append('form_type', filters.form_type);

      // Create a temporary form to submit with credentials
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = `${API_BASE_URL}/export_pdf.php`;
      form.target = '_blank';

      // Add query parameters as hidden inputs
      queryParams.forEach((value, key) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('admin_user');
    if (onLogout) {
      onLogout();
    }
    navigate('/admin');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
    setViewingPDFInModal(null);
  };

  const closePDFViewer = () => {
    setViewingPDFStandalone(null);
  };

  const handleViewPDF = async (id) => {
    try {
      const response = await getSubmissionDetails(id);
      if (response.success && response.submission.files.length > 0) {
        // Prepare all files with download URLs
        const filesWithUrls = response.submission.files.map(file => ({
          ...file,
          download_url: getDownloadUrl(file.id, true),
          size_formatted: formatFileSize(file.file_size)
        }));

        // Open FileViewerModal with all files
        setViewingPDFStandalone({
          files: filesWithUrls,
          submission: response.submission
        });
      } else {
        alert('No files found in this submission');
      }
    } catch (error) {
      alert('Failed to load files: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (!user) {
    navigate('/admin');
    return null;
  }

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h1 className="page-title">All Submissions</h1>
            <p className="page-subtitle">View and manage all work document submissions</p>
            {selectedIds.length > 0 && (
              <p style={{
                marginTop: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#6366f1'
              }}>
                {selectedIds.length} submission(s) selected
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {selectedIds.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="modern-btn danger"
                style={{
                  height: 'fit-content',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: '#dc2626',
                  color: '#fff'
                }}
              >
                {isDeleting ? '⏳ Deleting...' : `🗑️ Delete Selected (${selectedIds.length})`}
              </button>
            )}
            <button
              onClick={handleExportExcel}
              className="modern-btn secondary"
              style={{ height: 'fit-content', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              📊 Export Excel
            </button>
            <button
              onClick={handleExportPDF}
              className="modern-btn secondary"
              style={{ height: 'fit-content', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              📄 Export PDF
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="modern-btn secondary"
              style={{ height: 'fit-content' }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section" style={{ marginBottom: '20px' }}>
          {/* General Search Row */}
          <div className="filter-row" style={{ marginBottom: '12px' }}>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="🔍 Quick search across all fields... (auto-search)"
              className="modern-input"
              style={{ flex: '1' }}
              title="Search automatically applies after you stop typing"
            />
          </div>

          {/* Granular Filters Row */}
          <div className="filter-row" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <input
              type="text"
              name="worker_name"
              value={filters.worker_name}
              onChange={handleFilterChange}
              placeholder="👤 Filter by worker name..."
              className="modern-input"
              title="Search by worker name only"
            />
            <input
              type="text"
              name="job_site"
              value={filters.job_site}
              onChange={handleFilterChange}
              placeholder="🏗️ Filter by job site..."
              className="modern-input"
              title="Search by job site only"
            />
            <input
              type="text"
              name="comments"
              value={filters.comments}
              onChange={handleFilterChange}
              placeholder="💬 Filter by comments..."
              className="modern-input"
              title="Search by comments only"
            />
          </div>

          {/* Date and Type Filters Row */}
          <div className="filter-row" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>From Date</label>
              <input
                type="date"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
                className="modern-input"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>To Date</label>
              <input
                type="date"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
                className="modern-input"
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Form Type</label>
              <select
                name="form_type"
                value={filters.form_type}
                onChange={handleFilterChange}
                className="modern-input"
              >
                <option value="">All Form Types</option>
                <option value="simple">Daily Log</option>
                <option value="Concrete Safety Daily Log">Concrete Safety</option>
                <option value="Facade Daily Log">Facade Daily Log</option>
                <option value="Fire Safety Manager Daily Log">Fire Safety Manager</option>
                <option value="Site Safety Coordinator Daily Log">Site Safety Coordinator</option>
                <option value="Site Safety Manager Daily Log">Site Safety Manager</option>
                <option value="Superintendent Daily Log">Superintendent Log</option>
                <option value="Weekly TimeSheet">Weekly Timesheet</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignSelf: 'flex-end' }}>
              <button
                onClick={handleApplyFilters}
                className="modern-btn primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                Apply Filters
              </button>
              <button
                onClick={handleResetFilters}
                className="modern-btn secondary"
                style={{ whiteSpace: 'nowrap' }}
              >
                Reset All
              </button>
            </div>
          </div>

          {/* Active Filters Indicator */}
          {(filters.search || filters.worker_name || filters.job_site || filters.comments ||
            filters.from_date || filters.to_date || filters.form_type) && (
            <div style={{
              marginTop: '12px',
              padding: '8px 12px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#1e40af',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <span style={{ fontWeight: '600' }}>🔎 Active Filters:</span>
              {filters.search && <span className="filter-badge">Quick Search: "{filters.search}"</span>}
              {filters.worker_name && <span className="filter-badge">Worker: "{filters.worker_name}"</span>}
              {filters.job_site && <span className="filter-badge">Job Site: "{filters.job_site}"</span>}
              {filters.comments && <span className="filter-badge">Comments: "{filters.comments}"</span>}
              {filters.from_date && <span className="filter-badge">From: {filters.from_date}</span>}
              {filters.to_date && <span className="filter-badge">To: {filters.to_date}</span>}
              {filters.form_type && <span className="filter-badge">Type: {filters.form_type}</span>}
            </div>
          )}
        </div>

        {error && (
          <div className="error-banner">
            <span>⚠</span> {error}
          </div>
        )}

        {loading ? (
          <AllSubmissionsSkeleton />
        ) : (
          <>
            <DataTable
              title="All Work Documents"
              searchable={false}
              selectedIds={selectedIds}
              getRowId={(row) => row.id}
              columns={[
                {
                  key: 'checkbox',
                  label: (
                    <input
                      type="checkbox"
                      checked={selectedIds.length === submissions.length && submissions.length > 0}
                      onChange={toggleSelectAll}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#6366f1'
                      }}
                      title="Select all"
                    />
                  ),
                  sortable: false,
                  render: (value, row) => (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleSelectSubmission(row.id);
                      }}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#6366f1'
                      }}
                      title="Select this row"
                    />
                  )
                },
                {
                  key: 'row_number',
                  label: '#',
                  sortable: false,
                  render: (value, row, index) => {
                    const rowNumber = ((filters.page - 1) * (pagination?.per_page || 20)) + index + 1;
                    return <span style={{ fontWeight: 600, color: '#6b7280' }}>{rowNumber}</span>;
                  }
                },
                {
                  key: 'submission_date',
                  label: 'Date',
                  sortable: true,
                  render: (value) => formatDateEST(value)
                },
                {
                  key: 'worker_name',
                  label: 'Worker Name',
                  sortable: true
                },
                {
                  key: 'job_site',
                  label: 'Job Site',
                  sortable: true
                },
                {
                  key: 'form_type',
                  label: 'Form Type',
                  sortable: true,
                  render: (value) => {
                    const formInfo = getFormTypeLabel(value);
                    return (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        backgroundColor: `${formInfo.color}15`,
                        color: formInfo.color,
                        border: `1px solid ${formInfo.color}40`
                      }}>
                        <span>{formInfo.icon}</span>
                        <span>{formInfo.label}</span>
                      </span>
                    );
                  }
                },
                {
                  key: 'comments',
                  label: 'Comments',
                  render: (value) => truncateText(value)
                },
                {
                  key: 'file_count',
                  label: 'Files',
                  render: (value) => (
                    <span className="status-badge success">{value}</span>
                  )
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => {
                    const isLoading = loadingSubmissionId === row.id;
                    return (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewPDF(row.id);
                          }}
                          disabled={isLoading}
                          className="table-action-btn primary"
                          title={isLoading ? "Loading..." : "View PDF"}
                          style={{
                            opacity: isLoading ? 0.6 : 1,
                            cursor: isLoading ? 'wait' : 'pointer',
                            position: 'relative'
                          }}
                        >
                          {isLoading ? (
                            <>
                              <span style={{
                                display: 'inline-block',
                                width: '12px',
                                height: '12px',
                                border: '2px solid #ffffff',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 0.6s linear infinite',
                                marginRight: '4px'
                              }}></span>
                              Loading...
                            </>
                          ) : (
                            <>👁️ View</>
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFiles(row);
                          }}
                          className="table-action-btn"
                          style={{
                            backgroundColor: '#059669',
                            color: 'white',
                            border: '1px solid #047857'
                          }}
                          title="Download Attached Files"
                        >
                          ↓ Download
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(row.id);
                          }}
                          className="table-action-btn danger"
                          title="Delete Submission"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    );
                  }
                }
              ]}
              data={submissions}
            />

            {pagination && pagination.total_pages > 1 && (
              <div className="modern-pagination">
                <button
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={!pagination.has_prev}
                  className="pagination-btn"
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_count} total)
                </span>
                <button
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={!pagination.has_next}
                  className="pagination-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {/* Submission Details Modal */}
        {showModal && (
          <div className="modern-modal-overlay" onClick={closeModal}>
            <div
              className="modern-modal"
              onClick={(e) => e.stopPropagation()}
              style={viewingPDFInModal ? { maxWidth: '95%', width: '1400px', height: '90vh' } : {}}
            >
              <div className="modern-modal-header">
                <h2>Submission Details</h2>
                <button onClick={closeModal} className="modal-close-btn">
                  ✕
                </button>
              </div>

              {loadingDetails ? (
                <div className="modern-modal-body" style={{ padding: '24px' }}>
                  {/* Skeleton Loading UI */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}>
                    {/* Header skeleton */}
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{
                        display: 'inline-block',
                        width: '50px',
                        height: '50px',
                        border: '4px solid #f3f4f6',
                        borderTop: '4px solid #6366f1',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '16px'
                      }}></div>
                      <p style={{
                        color: '#6366f1',
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0'
                      }}>
                        Loading submission details...
                      </p>
                      <p style={{
                        color: '#9ca3af',
                        fontSize: '14px',
                        margin: '8px 0 0 0'
                      }}>
                        Please wait while we fetch the data
                      </p>
                    </div>

                    {/* Detail grid skeleton */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px'
                    }}>
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} style={{ padding: '12px' }}>
                          <div style={{
                            height: '14px',
                            width: '60%',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}></div>
                          <div style={{
                            height: '20px',
                            width: '90%',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '4px'
                          }}></div>
                        </div>
                      ))}
                    </div>

                    {/* Comments skeleton */}
                    <div style={{ padding: '12px' }}>
                      <div style={{
                        height: '14px',
                        width: '80px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        marginBottom: '8px'
                      }}></div>
                      <div style={{
                        height: '60px',
                        width: '100%',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px'
                      }}></div>
                    </div>

                    {/* Files skeleton */}
                    <div>
                      <div style={{
                        height: '18px',
                        width: '150px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        marginBottom: '16px'
                      }}></div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '12px'
                      }}>
                        {[1, 2, 3].map((i) => (
                          <div key={i} style={{
                            height: '80px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '8px'
                          }}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : selectedSubmission ? (
              <div
                className="modern-modal-body"
                style={viewingPDFInModal ? {
                  maxHeight: 'calc(90vh - 180px)',
                  overflowY: 'auto'
                } : {}}
              >
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Worker Name</label>
                    <p>{selectedSubmission.worker_name}</p>
                  </div>
                  <div className="detail-item">
                    <label>Job Site</label>
                    <p>{selectedSubmission.job_site}</p>
                  </div>
                  <div className="detail-item">
                    <label>Date</label>
                    <p>{formatDateEST(selectedSubmission.submission_date)}</p>
                  </div>
                  <div className="detail-item">
                    <label>Form Type</label>
                    <p>
                      {(() => {
                        const formInfo = getFormTypeLabel(selectedSubmission.form_type);
                        return (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            backgroundColor: `${formInfo.color}15`,
                            color: formInfo.color,
                            border: `1px solid ${formInfo.color}40`
                          }}>
                            <span>{formInfo.icon}</span>
                            <span>{formInfo.label}</span>
                          </span>
                        );
                      })()}
                    </p>
                  </div>
                </div>

                <div className="detail-item full-width">
                  <label>Comments</label>
                  <p>{selectedSubmission.comments || 'No comments'}</p>
                </div>

                <div className="files-section-modern">
                  <h3>Attached Files ({selectedSubmission.files.length})</h3>
                  {selectedSubmission.files.length > 0 ? (
                    <div className="files-grid">
                      {selectedSubmission.files.map((file) => {
                        const ext = file.file_name.split('.').pop().toLowerCase();
                        const isPDF = ext === 'pdf';

                        return (
                          <div key={file.id} className="file-card">
                            <div className="file-card-icon">
                              {isPDF ? '📕' : '📄'}
                            </div>
                            <div className="file-card-info">
                              <div className="file-card-name">{file.file_name}</div>
                              <div className="file-card-size">{formatFileSize(file.file_size)}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              {isPDF && (
                                <button
                                  onClick={() => setViewingPDFInModal({
                                    id: file.id,
                                    name: file.file_name,
                                    url: getDownloadUrl(file.id, true)
                                  })}
                                  className="file-download-btn"
                                  style={{ background: '#0891b2', cursor: 'pointer', border: 'none' }}
                                  title="View PDF"
                                >
                                  👁
                                </button>
                              )}
                              <a
                                href={getDownloadUrl(file.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-download-btn"
                                title="Download file"
                              >
                                ↓
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-files">No files attached</p>
                  )}
                </div>

                {/* PDF Viewer Section */}
                {viewingPDFInModal && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                      paddingBottom: '12px',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>
                        📄 {viewingPDFInModal.name}
                      </h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => {
                            const pdfContainer = document.getElementById('pdf-viewer-container');
                            if (pdfContainer) {
                              if (document.fullscreenElement) {
                                document.exitFullscreen();
                              } else {
                                pdfContainer.requestFullscreen();
                              }
                            }
                          }}
                          className="modern-btn"
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px',
                            background: '#10b981',
                            color: '#fff'
                          }}
                          title="View Fullscreen"
                        >
                          ⛶ Fullscreen
                        </button>
                        <a
                          href={viewingPDFInModal.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="modern-btn"
                          style={{
                            textDecoration: 'none',
                            padding: '6px 12px',
                            fontSize: '13px',
                            background: '#6366f1',
                            color: '#fff'
                          }}
                        >
                          ↓ Download
                        </a>
                        <button
                          onClick={() => setViewingPDFInModal(null)}
                          className="modern-btn secondary"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          ✕ Close PDF
                        </button>
                      </div>
                    </div>
                    <div
                      id="pdf-viewer-container"
                      style={{
                        width: '100%',
                        height: '600px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        background: '#fff'
                      }}
                    >
                      {/* Exit Fullscreen Button - Shows only in fullscreen */}
                      <button
                        onClick={() => {
                          if (document.fullscreenElement) {
                            document.exitFullscreen();
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          zIndex: 1000,
                          padding: '10px 16px',
                          background: 'rgba(0, 0, 0, 0.8)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'none',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          transition: 'background 0.2s ease'
                        }}
                        className="fullscreen-exit-btn"
                        onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.9)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.8)'}
                      >
                        ✕ Exit Fullscreen
                      </button>
                      <iframe
                        src={viewingPDFInModal.url}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        title={viewingPDFInModal.name}
                      />
                    </div>
                  </div>
                )}
              </div>
              ) : null}

              {!loadingDetails && selectedSubmission && (
              <div className="modern-modal-footer">
                <button onClick={closeModal} className="modern-btn secondary">
                  Close
                </button>
                <button
                  onClick={() => handleDelete(selectedSubmission.id)}
                  className="modern-btn danger"
                >
                  Delete Submission
                </button>
              </div>
              )}
            </div>
          </div>
        )}

        {/* File Viewer Modal - Smart viewer for all file types */}
        {viewingPDFStandalone && viewingPDFStandalone.files && (
          <FileViewerModal
            files={viewingPDFStandalone.files}
            initialIndex={0}
            onClose={closePDFViewer}
          />
        )}
      </div>
    </Layout>
  );
}

export default AllSubmissions;
